// Input.tsx
// Composant champ de saisie générique avec label, gestion d'erreur et texte d'aide.
// Updated: added useId to link label htmlFor to input id for accessibility.
// Updated: added disabled state styling (bg-neutral-50, muted text, not-allowed cursor).
// Updated: added optional iconRight prop for displaying an icon inside the right side of the input.

import { ReactNode, useId, InputHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  iconRight?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, iconRight, className = '', id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-neutral-700 mb-2">
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent shadow-soft transition-all disabled:bg-neutral-50 disabled:text-neutral-500 disabled:cursor-not-allowed ${
              error ? 'border-red-500' : 'border-neutral-200'
            } ${iconRight ? 'pr-12' : ''} ${className}`}
            {...props}
          />
          {iconRight && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              {iconRight}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-xs text-neutral-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
